import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import API_BASE from "./config";
import Pagination from "./components/Pagination";
import Modal from "react-modal";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { usePermissions, RequirePermission } from "./hooks/usePermissions";
import "./css/UserData.css";
import "./css/LaunchBuild.css";

const LaunchBuild = ({ chatId, workflowId, workflowMetadataId, cognitoUserId, organization }) => {
  const [workflowData, setWorkflowData] = useState(null);
  const [svgContent, setSvgContent] = useState("");
  const [svgUrl, setSvgUrl] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [existingFiles, setExistingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  const filesPerPage = 20;

  // Human-readable file size (bytes -> B/KB/MB/GB/TB).
  const formatSize = (bytes) => {
    if (bytes == null || isNaN(bytes)) return "—";
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  useEffect(() => {
    const fetchWorkflowDetails = async () => {
      try {
        const res = await axios.get(`${API_BASE}/get-workflow-details/${workflowMetadataId}`);
        const details = res.data.workflowDetails[0];
        setWorkflowData(details);

        if (details?.workflow_svg_path) {
          const svgRes = await fetch(`${API_BASE}/${details.workflow_svg_path}`);
          if (svgRes.ok) {
            const svgText = await svgRes.text();
            setSvgContent(svgText);
          }

          const svgFolder = details.workflow_svg_path.split("/").slice(0, -1).join("/");
          const mainSvgPath = `${svgFolder}/main.svg`;
          const mainSvgRes = await fetch(`${API_BASE}/${mainSvgPath}`);
          if (mainSvgRes.ok) {
            setSvgUrl(`${API_BASE}/${mainSvgPath}`);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading workflow details:", err);
        setError("Failed to load workflow details.");
        setLoading(false);
      }
    };

    fetchWorkflowDetails();
  }, [workflowId, workflowMetadataId]);

  useEffect(() => {
    const fetchFiles = async () => {
      const idToken = localStorage.getItem("idToken");
      const res = await fetch(`${API_BASE}/user-files`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ cognitoUserId }),
      });
      const data = await res.json();
      const normalized = data.files.map((file) => ({
        id: file.id,
        name: file.filename,
        project: file.original_project,
        path: file.s3_folder_path,
        size: file.size,
        createdRaw: file.created_on,
        created: new Date(file.created_on).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
      }));
      setExistingFiles(normalized);
    };
    if (cognitoUserId) fetchFiles();
  }, [cognitoUserId]);

  const sortedFiles = useMemo(() => {
    if (!sortConfig.key) return existingFiles;
    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;
    return [...existingFiles].sort((a, b) => {
      let aVal, bVal;
      if (key === "created") {
        aVal = new Date(a.createdRaw || 0).getTime();
        bVal = new Date(b.createdRaw || 0).getTime();
      } else if (key === "size") {
        aVal = a.size == null ? -1 : a.size;
        bVal = b.size == null ? -1 : b.size;
      } else {
        aVal = (a[key] || "").toString().toLowerCase();
        bVal = (b[key] || "").toString().toLowerCase();
      }
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }, [existingFiles, sortConfig]);

  const filteredFiles = searchQuery.trim()
    ? sortedFiles.filter(
        (file) =>
          file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          file.project.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sortedFiles;

  const paginatedFiles = filteredFiles.slice(
    (currentPage - 1) * filesPerPage,
    currentPage * filesPerPage
  );

  // Select-all applies to every file in the current (filtered) result set.
  const allFilteredSelected =
    filteredFiles.length > 0 && filteredFiles.every((f) => selectedFiles.includes(f.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedFiles((prev) => prev.filter((id) => !filteredFiles.some((f) => f.id === id)));
    } else {
      setSelectedFiles((prev) =>
        Array.from(new Set([...prev, ...filteredFiles.map((f) => f.id)]))
      );
    }
  };

  const toggleRow = (id) => {
    setSelectedFiles((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="userdata-sort-icon" />;
    return sortConfig.direction === "asc"
      ? <FaSortUp className="userdata-sort-icon active" />
      : <FaSortDown className="userdata-sort-icon active" />;
  };

  if (loading) return <p>Loading workflow details...</p>;
  if (error) return <p>{error}</p>;

  // Interactive workflow diagram (NFViz), same applet as WorkflowDetails.
  const NFVIZ_BASE = process.env.REACT_APP_NFVIZ_BASE;
  const NFVIZ_ROOT = "/home/apps/cloudgenomics/backend";
  let workflowFolder = null;
  if (workflowData?.nextflow_path) {
    const relativeFolder = workflowData.nextflow_path.replace(/\/main\.nf$/, "");
    workflowFolder = `${NFVIZ_ROOT}${relativeFolder}`;
  }
  const embedUrl =
    NFVIZ_BASE && workflowFolder
      ? `${NFVIZ_BASE}/embed.html?type=local&theme=light&edit=on&grid=on&source=${encodeURIComponent(workflowFolder)}`
      : null;

  return (
    <div className="launchbuild">
      <h3>
        Launch <em>{workflowData?.short_name || "this workflow"}</em> with your data
      </h3>

      <h4>Workflow Summary</h4>
      {workflowData?.workflow_summary ? (
        <p>{workflowData.workflow_summary}</p>
      ) : (
        <p>No workflow summary available.</p>
      )}

      <h4>Workflow Diagram</h4>
      {embedUrl ? (
        <iframe
          src={embedUrl}
          title="Nextflow workflow"
          className="lb-diagram-frame"
          loading="lazy"
        />
      ) : svgUrl ? (
        <div className="lb-diagram-wrap">
          <img
            src={svgUrl}
            alt="Workflow DAG"
            className="lb-diagram-img"
          />
        </div>
      ) : (
        <p>Workflow diagram not available.</p>
      )}

      <RequirePermission permission="executeBuilds">
      <h4>
        Select Files (
        {workflowData?.input_files_list
          ? workflowData.input_files_list.split("|").join(", ")
          : "No file types specified"}
        )
      </h4>

      <p className="lb-hint">
        Selected {selectedFiles.length} file(s) of {workflowData?.input_files_list?.split("|").length || 0} required types
      </p>
      <input
        type="text"
        placeholder="Search by filename or project"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="lb-search"
      />

      <table className="userdata-table">
        <thead>
          <tr>
            <th className="userdata-th-select">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
                title="Select all"
              />
            </th>
            <th className="userdata-th-sort" onClick={() => requestSort("name")}>
              Filename {renderSortIcon("name")}
            </th>
            <th className="userdata-th-sort" onClick={() => requestSort("project")}>
              Project {renderSortIcon("project")}
            </th>
            <th className="userdata-th-sort" onClick={() => requestSort("size")}>
              Size {renderSortIcon("size")}
            </th>
            <th className="userdata-th-sort" onClick={() => requestSort("created")}>
              Created On {renderSortIcon("created")}
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedFiles.length === 0 ? (
            <tr>
              <td colSpan={5} className="userdata-empty">No files found.</td>
            </tr>
          ) : (
            paginatedFiles.map((f) => (
              <tr key={f.id} className={selectedFiles.includes(f.id) ? "selected" : ""}>
                <td className="userdata-td-select">
                  <input
                    type="checkbox"
                    checked={selectedFiles.includes(f.id)}
                    onChange={() => toggleRow(f.id)}
                  />
                </td>
                <td>{f.name}</td>
                <td>{f.project}</td>
                <td>{formatSize(f.size)}</td>
                <td>{f.created}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <Pagination
        page={currentPage}
        totalPages={Math.ceil(filteredFiles.length / filesPerPage)}
        onChange={setCurrentPage}
      />

      {/* Launch Analysis Button */}
      <div className="lb-launch-row">
        {(() => {
          const ready = selectedFiles.length > (workflowData?.input_files_list?.split("|").length || 0);
          return (
            <button
              onClick={() => setModalOpen(true)}
              disabled={!ready}
              className="lb-launch-btn"
              style={{
                backgroundColor: ready ? "#007BFF" : "#ccc",
                cursor: ready ? "pointer" : "not-allowed",
              }}
            >
              LAUNCH ANALYSIS
            </button>
          );
        })()}
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          },
          content: {
            width: "40%",
            height: "40%",
            margin: "auto",
            top: "30%",
            left: "30%",
            right: "30%",
            bottom: "30%",
            borderRadius: "10px",
            padding: "20px",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          },
        }}
      >
        <h2>Launch Analysis</h2>
        <p>
          THIS ACTION WILL LAUNCH ANALYSIS OF THE SELECTED FILES ON CLOUD. DO YOU WANT TO PROCEED?
        </p>
        <div className="lb-modal-actions">
          <button
            onClick={async () => {
              try {
                setModalOpen(false);

                // Collect selected file IDs
                const fileIds = selectedFiles;

                const payload = {
                  workflow_id: workflowData.id,          // workflow metadata id
                  cognitoUserId,                         // current user
                  organization,                          // org
                  fileIds                                // array of selected file IDs
                };

                // Designer workflows launch via their own endpoint (no Dockerfile /
                // no pre-generated config); generated/github use the standard one.
                const launchPath =
                  workflowData?.workflow_type === "designer"
                    ? "/launch-designer-userdata"
                    : "/launch-analysis-userdata";

                const res = await fetch(`${API_BASE}${launchPath}`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(payload),
                });

                if (res.ok) {
                  const data = await res.json();
                  alert(`✅ Analysis launched! Job ID: ${data.jobId}`);
                  // Optionally navigate to analyses page
                  window.location.href = "/analyses";
                } else {
                  const err = await res.json();
                  alert(`❌ Failed to launch analysis: ${err.error}`);
                }
              } catch (error) {
                console.error("❌ Error launching analysis:", error);
                alert("❌ Error launching analysis.");
              }
            }}
            className="lb-btn-yes"
          >
            YES
          </button>
          <button
            onClick={() => setModalOpen(false)}
            className="lb-btn-no"
          >
            NO
          </button>
        </div>
      </Modal>
      </RequirePermission>
    </div>
  );
};

export default LaunchBuild;
